// Drawing a seasonal design: its words and illustration as one SVG, 1080 by
// 1350 for a post and 1080 by 1920 for a Story, which the post canvas draws
// as a picture. The twelve arrangements are the campaign library's own
// (Ben with Codex, 30 Sept 2026), taking the post's words and the salon's name.
//
// A Story is the post's design, a little smaller, with every word inside the
// band Instagram leaves clear (270 to 1250 down, 65 in from each side); what
// runs to the edges of the post (a column, a colour block, a pattern) still
// runs to the edges of the Story.
import { illustration } from './art'
import { seasonalOf } from '.'

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }
const esc = s => String(s).replace(/[&<>"']/g, c => ESCAPES[c])
const WIDTHS = { i: 0.23, l: 0.25, j: 0.25, t: 0.34, f: 0.32, r: 0.36, m: 0.82, w: 0.76, I: 0.3, M: 0.89, W: 0.95, ' ': 0.27 }
const textWidth = (s, size) => [...s].reduce((n, c) => n + (WIDTHS[c] || (/[A-Z]/.test(c) ? 0.64 : 0.51)), 0) * size
const linesFor = (s, w, size) => {
  const lines = ['']
  s.split(/\s+/).forEach((word) => {
    const i = lines.length - 1
    const next = lines[i] ? `${lines[i]} ${word}` : word
    if (textWidth(next, size) > w && lines[i]) lines.push(word)
    else lines[i] = next
  })
  return lines
}
// Words that wrap to their width and shrink to fit their height
const copy = (s, { x, y, w, h, size = 90, fill, font = 'Georgia, serif', align = 'start', weight = 400 }) => {
  if (!s) return ''
  const fits = (ls, sz) => ls.length * sz * 1.08 <= h && ls.every(l => textWidth(l, sz) <= w)
  let lines = linesFor(s, w, size)
  while (!fits(lines, size) && size > 24) {
    size -= 2
    lines = linesFor(s, w, size)
  }
  return `<text fill="${fill}" font-family="${font}" font-size="${size}" font-weight="${weight}" text-anchor="${align}">${lines.map((l, i) => `<tspan x="${x}" y="${y + size * 0.85 + i * size * 1.08}">${esc(l)}</tspan>`).join('')}</text>`
}
const label = (s, x, y, col, anchor = 'start', size = 24) => (s ? `<text x="${x}" y="${y}" text-anchor="${anchor}" fill="${col}" font-family="Arial, sans-serif" font-size="${size}" font-weight="600" letter-spacing="3">${esc(s.toUpperCase())}</text>` : '')
const rect = (x, y, w, h, fill, r = 0) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${fill}"/>`
const circle = (x, y, r, fill) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`
// Dark words on a light colour, light words on a dark one
const wordsOn = (hex) => {
  const [r, g, b] = hex.replace('#', '').match(/../g).map((x) => { const n = parseInt(x, 16) / 255; return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4 })
  return r * 0.2126 + g * 0.7152 + b * 0.0722 > 0.35 ? '#201e22' : '#fff8ec'
}
const plain = s => String(s || '').replace(/\*/g, '').trim()

// One design as SVG markup. `words` are the post's: eyebrow (the small
// heading), headline, body, cta, and the salon's name ('' for none).
const STORY = { k: 980 / 1200, x: 99, y: 272 - 72 * (980 / 1200) }

export const seasonalSvg = (season, design, format = 'post', words = {}) => {
  const story = format === 'story'
  const W = 1080
  const H = 1350
  const top = 72
  const space = 1200
  // Where a point of the post lands in the Story
  const sx = v => STORY.x + v * STORY.k
  const sy = v => STORY.y + v * STORY.k
  const [ink, accent, paper, soft, gold] = season.palette
  const salon = words.salon == null ? 'Your salon' : words.salon
  const say = s => plain(s).replace(/\{salon\}/g, salon || 'us')
  const title = say(words.headline != null ? words.headline : design.headline)
  const body = say(words.body != null ? words.body : design.body)
  const cta = say(words.cta != null ? words.cta : design.cta)
  const eyebrow = say(words.eyebrow != null ? words.eyebrow : season.name)
  const art = (x, y, size, p = season.palette) => `<g transform="translate(${x} ${y}) scale(${size / 500})">${illustration(design.motif, p)}</g>`
  const heading = (x, y, w, h, col, size = 100, align = 'start') => copy(title, { x, y, w, h, fill: col, size, align })
  const bodyAt = (x, y, w, col, align = 'start') => copy(body, { x, y, w, h: 108, size: 34, fill: col, align, font: 'Arial, sans-serif' })
  const footer = (col, y = top + space - 20) => label(salon, 540, y, col, 'middle', 25)
  const button = (x, y, col, fill = null, w = 600) => (cta ? `${fill ? rect(x - w / 2, y - 40, w, 66, fill, 33) : ''}${label(cta, x, y, col, 'middle', 25)}` : '')
  const fine = col => `<path d="M82 ${top + space - 83}H998" stroke="${col}" opacity=".28"/>`
  let bg = paper
  let drawn = ''
  let bleed = '' // a Story's own edge-to-edge shapes, under the design
  switch (design.layout) {
    case 0: // Bold launch poster, a large illustration under the headline
      bg = ink
      drawn = label(eyebrow, 82, top + 24, paper) + heading(82, top + 84, 910, 330, paper, 118) + art(255, top + 370, 680) + bodyAt(82, top + space - 254, 916, paper) + fine(paper) + footer(paper)
      break
    case 1: // An arch of illustration over a centred caption
      drawn = label(eyebrow, 540, top + 24, ink, 'middle') + `<path d="M176 ${top + 590}V${top + 280}A364 260 0 0 1 904 ${top + 280}V${top + 590}Z" fill="${soft}"/>` + art(302, top + 48, 475, [ink, accent, paper, paper, gold]) + heading(540, top + 649, 885, 245, ink, 100, 'middle') + bodyAt(540, top + space - 225, 850, ink, 'middle') + footer(ink)
      break
    case 2: { // Asymmetric two columns
      bg = accent
      const col = wordsOn(accent)
      bleed = rect(sx(655), 0, W - sx(655), 1920, paper)
      drawn = rect(655, 0, 425, H, paper) + label(eyebrow, 76, top + 23, col) + heading(76, top + 180, 535, 650, col, 113) + art(573, top + 274, 530) + bodyAt(76, top + space - 263, 535, col) + label(salon, 76, top + space - 18, col, 'start', 24)
      break
    }
    case 3: // A framed still life
      drawn = `<rect x="42" y="${top - 12}" width="996" height="${space}" rx="220" fill="none" stroke="${gold}" stroke-width="3"/>` + label(eyebrow, 540, top + 85, ink, 'middle') + art(225, top + 160, 630) + heading(540, top + space - 472, 870, 230, ink, 95, 'middle') + bodyAt(540, top + space - 208, 830, ink, 'middle') + footer(ink, top + space - 56)
      break
    case 4: // A colour-block spread
      bg = soft
      bleed = rect(0, sy(top + space * 0.49), W, 1920, ink)
      drawn = rect(0, top + space * 0.49, W, H, ink) + art(285, top + 24, 510) + label(eyebrow, 82, top + space * 0.49 + 62, paper) + heading(82, top + space * 0.49 + 107, 916, 290, paper, 108) + bodyAt(82, top + space - 216, 890, paper) + footer(paper)
      break
    case 5: // A conversation card, with motifs in the corners
      drawn = art(-58, top + 50, 355) + art(790, top + space - 430, 340) + label(eyebrow ? `Let’s talk · ${eyebrow}` : '', 540, top + 28, ink, 'middle', 22) + heading(540, top + 328, 815, 360, ink, 105, 'middle') + bodyAt(540, top + 753, 745, ink, 'middle') + button(540, top + space - 157, paper, ink, 630) + footer(ink)
      break
    case 6: // A feature card with an illustrated medallion
      bg = ink
      drawn = rect(64, top + 47, 952, space - 166, paper, 26) + circle(540, top + 292, 188, soft) + art(361, top + 104, 358) + label(eyebrow, 540, top + 554, ink, 'middle', 23) + heading(540, top + 618, 828, 244, ink, 95, 'middle') + bodyAt(540, top + space - 335, 820, ink, 'middle') + button(540, top + space - 192, ink) + footer(paper)
      break
    case 7: { // A wrapping-paper border of the motif
      bg = accent
      const col = wordsOn(accent)
      const pattern = rows => Array.from({ length: rows * 4 }, (_, i) => art((i % 4) * 310 - 74, Math.floor(i / 4) * 480 - 20, 270, [ink, paper, paper, soft, gold])).join('')
      bleed = pattern(5)
      drawn = (story ? '' : pattern(3)) + rect(86, top + 178, 908, space - 356, paper, 5) + label(eyebrow, 540, top + 251, ink, 'middle', 23) + heading(540, top + 335, 764, 410, ink, 102, 'middle') + bodyAt(540, top + space - 478, 746, ink, 'middle') + button(540, top + space - 258, ink) + footer(col)
      break
    }
    case 8: // An advice column with a small illustration
      drawn = label(eyebrow, 82, top + 26, ink) + heading(82, top + 126, 911, 390, ink, 120) + `<path d="M82 ${top + 563}H998" stroke="${gold}" stroke-width="3"/>` + bodyAt(82, top + 651, 440, ink) + art(549, top + 613, 450) + button(350, top + space - 151, ink, null, 400) + footer(ink)
      break
    case 9: // A ticket to book
      bg = soft
      drawn = rect(94, top + 42, 892, space - 147, paper, 40) + label(eyebrow, 540, top + 125, ink, 'middle', 24) + art(340, top + 166, 400) + heading(540, top + 598, 773, 247, ink, 100, 'middle') + bodyAt(540, top + space - 328, 770, ink, 'middle') + `<path d="M134 ${top + space - 192}H946" stroke="${gold}" stroke-width="2" stroke-dasharray="9 12"/>` + circle(93, top + space - 192, 27, soft) + circle(987, top + space - 192, 27, soft) + button(540, top + space - 139, ink) + footer(ink)
      break
    case 10: // Small, confident words under a large illustration
      bg = ink
      drawn = label(eyebrow, 540, top + 24, paper, 'middle') + art(185, top + 116, 710) + heading(82, top + space - 451, 916, 195, paper, 93) + bodyAt(82, top + space - 208, 900, paper) + footer(paper)
      break
    default: // A greeting to close
      bleed = rect(0, 0, W, sy(top + space * 0.53), soft)
      drawn = rect(0, 0, W, top + space * 0.53, soft) + art(265, top + 38, 550) + heading(540, top + space * 0.53 + 75, 874, 285, ink, 107, 'middle') + bodyAt(540, top + space - 186, 850, ink, 'middle') + footer(ink)
  }
  if (!story) return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">${rect(0, 0, W, H, bg)}${drawn}</svg>`
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="1920" viewBox="0 0 ${W} 1920">${rect(0, 0, W, 1920, bg)}${bleed}<g transform="translate(${STORY.x} ${STORY.y.toFixed(2)}) scale(${STORY.k.toFixed(5)})">${drawn}</g></svg>`
}

// The post's seasonal design as a picture to load, in a format
export const seasonalSrc = (post, { name, brandMode } = {}, format = post.format || 'post') => {
  const found = seasonalOf(post.layout)
  if (!found) return null
  const svg = seasonalSvg(found.season, found.design, format, { ...(post.content || {}), salon: brandMode === 'none' ? '' : (name || 'Your salon') })
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

// On the post canvas: the picture, full frame. While a change to the words
// loads, the design's last picture stays up (the season's ground before the
// first).
const last = new Map()
export const drawSeasonal = (p, env) => {
  if (p.isDry) return
  const found = seasonalOf(env.post.layout)
  const shown = `${env.post.layout}|${env.format}`
  const img = env.images[seasonalSrc(env.post, env.theme, env.format)] || last.get(shown)
  if (img) {
    last.set(shown, img)
    p.ctx.drawImage(img, 0, 0, env.W, env.H)
  } else if (found) p.rect(0, 0, env.W, env.H, found.season.palette[2])
}
